import { Box, Button, CircularProgress } from "@mui/material"
import { StepperComponents } from "./UploadComponent"
import * as hooks from "lib/hooks"
import { motion } from "framer-motion"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import * as result from "lib/result"
import { LoadingButtonStyle } from "lib/styles/button"

export interface StepButtonsProps {
	nextDisabled?: bool
	backDisabled?: bool
	onNext?: () => void
	onBack?: () => void
	nextLoading?: bool
	nextHidden?: bool
	backText?: string
	nextText?: string
	nextVariant?: "text" | "contained" | "outlined"
	showCancel?: bool
	onCancel?: () => void
	cancelText?: string
}

interface loadingButtonProps extends React.ComponentProps<typeof Button> {
	loading?: bool
	loadingElement?: React.ReactNode
}

export function LoadingButton({ loading, children, loadingElement, disabled, ...props }: loadingButtonProps) {
	const { theme } = hooks.UseTheme()
	return (
		<Button
			data-test-selector="next-button"
			{...props}
			disabled={Boolean(loading) || Boolean(disabled)}
			sx={LoadingButtonStyle(theme)}
		>
			{loading ? loadingElement || <CircularProgress size={24} color="inherit" /> : children}
		</Button>
	)
}

export function StepButtons({
	nextDisabled = true,
	backDisabled = false,
	nextLoading = false,
	nextHidden = false,
	backText = "Back",
	nextText = "Next",
	nextVariant = "contained",
	showCancel = false,
	cancelText = "Cancel",
	...props
}: StepButtonsProps) {
	const { activeStep, setActiveStep, setStepStatus } = hooks.UseFormContext()

	const handleBack = () => {
		if (activeStep > 0) {
			if (props.onBack) {
				props.onBack()
			} else {
				setActiveStep((p) => p - 1)
			}
			setStepStatus(result.Ok({ formProgress: "pending" }))
		}
	}

	const onNext = () => {
		if (activeStep < StepperComponents.length) {
			if (props.onNext) {
				props.onNext()
			} else {
				setActiveStep((p) => p + 1)
			}
		}
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "row",
				gap: 2,
			}}
		>
			{showCancel && props.onCancel && (
				<Button
					variant="outlined"
					color="inherit"
					onClick={props.onCancel}
					data-test-selector="cancel-button"
					sx={{
						mr: "auto",
						px: 4,
						py: 1.2,
						borderRadius: 20,
						textTransform: "none",
						fontSize: "0.95rem",
						fontWeight: 400,
						letterSpacing: "0.01em",
					}}
				>
					{cancelText}
				</Button>
			)}

			{activeStep > 0 && !backDisabled && (
				<Button
					color="inherit"
					onClick={handleBack}
					data-test-selector="back-button"
				>
					{backText}
				</Button>
			)}

			{activeStep < StepperComponents.length &&
				!nextHidden &&
				(nextDisabled ? (
					<LoadingButton
						loading={nextLoading}
						disabled={true}
						onClick={onNext}
						variant={nextVariant}
						endIcon={<ArrowForwardIcon />}
						sx={{
							px: 4,
							py: 1.2,
							borderRadius: 20,
							textTransform: "none",
							fontSize: "0.95rem",
							fontWeight: 400,
							letterSpacing: "0.01em",
						}}
						loadingElement={<CircularProgress size={24} color="inherit" />}
					>
						{nextText}
					</LoadingButton>
				) : (
					<LoadingButton
						loading={nextLoading}
						onClick={onNext}
						variant={nextVariant}
						endIcon={<ArrowForwardIcon fontSize="small" />}
						sx={{
							px: 4,
							py: 1.2,
							borderRadius: 20,
							textTransform: "none",
							fontSize: "0.95rem",
							fontWeight: 400,
							letterSpacing: "0.01em",
							transition: "all 0.2s ease",
						}}
						loadingElement={<CircularProgress size={24} color="inherit" />}
					>
						{nextText}
					</LoadingButton>
				))}
		</Box>
	)
}
